/**
 * Recherche globale : agrège les endpoints `search` existants de chaque module.
 * Aucun nouvel endpoint backend — on réutilise les listes filtrées par `search`.
 */
import { appointmentsApi } from "@/features/appointments/api";
import { clientsApi } from "@/features/clients/api";
import { familyApi } from "@/features/family/api";
import { inventoryApi } from "@/features/inventory/api";
import { patientsApi } from "@/features/patients/api";
import { salesApi } from "@/features/sales/api";
import { formatDateTimeFr, formatMoney } from "@/lib/utils";

import type { SearchResultItem } from "./types";

/** Nombre maximum de résultats affichés par module. */
export const SEARCH_LIMIT = 6;

export async function searchPatients(q: string): Promise<SearchResultItem[]> {
  const res = await patientsApi.list({ search: q });
  return res.results.slice(0, SEARCH_LIMIT).map((p) => ({
    type: "patient" as const,
    id: p.id,
    title: [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || p.patient_code,
    subtitle: [p.patient_code, p.phone].filter(Boolean).join(" · "),
    to: `/patients/${p.id}`,
  }));
}

export async function searchClients(q: string): Promise<SearchResultItem[]> {
  const res = await clientsApi.list({ search: q });
  return res.results.slice(0, SEARCH_LIMIT).map((c) => ({
    type: "client" as const,
    id: c.id,
    title:
      c.display_name ||
      [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
      c.client_code,
    subtitle: [c.client_code, c.phone].filter(Boolean).join(" · "),
    to: `/clients/${c.id}`,
  }));
}

/**
 * Foyers (patients + clients au même numéro). Un foyer n'est pas une entité
 * en base : il n'a donc pas de route propre. On renvoie vers le répertoire
 * pré-filtré sur son numéro, qui n'isole que ce foyer (`list_families`
 * matche les chiffres de la recherche dans la clé téléphone).
 */
export async function searchFamilies(q: string): Promise<SearchResultItem[]> {
  const res = await familyApi.list({ search: q, page: 1, page_size: SEARCH_LIMIT });
  return res.results.slice(0, SEARCH_LIMIT).map((f) => ({
    type: "family" as const,
    id: f.phone,
    title: f.label || "Famille",
    subtitle: [
      `${f.member_count} membre${f.member_count > 1 ? "s" : ""}`,
      f.phone_display,
    ]
      .filter(Boolean)
      .join(" · "),
    to: `/familles?search=${encodeURIComponent(f.phone_display || f.phone)}`,
  }));
}

export async function searchSales(q: string): Promise<SearchResultItem[]> {
  const res = await salesApi.list({ search: q });
  return res.results.slice(0, SEARCH_LIMIT).map((s) => ({
    type: "sale" as const,
    id: s.id,
    title: s.invoice_number || "Facture",
    subtitle: [s.patient_name, formatMoney(s.total_amount)].filter(Boolean).join(" · "),
    to: `/factures/${s.id}`,
  }));
}

export async function searchProducts(q: string): Promise<SearchResultItem[]> {
  const res = await inventoryApi.list({ search: q });
  return res.results.slice(0, SEARCH_LIMIT).map((p) => ({
    type: "product" as const,
    id: p.id,
    title: p.name,
    subtitle: [p.sku, formatMoney(p.sale_price)].filter(Boolean).join(" · "),
    to: `/catalogue/${p.id}`,
  }));
}

export async function searchAppointments(q: string): Promise<SearchResultItem[]> {
  const res = await appointmentsApi.list({ search: q });
  return res.results.slice(0, SEARCH_LIMIT).map((a) => ({
    type: "appointment" as const,
    id: a.id,
    title: a.patient_name || a.reference || "Rendez-vous",
    subtitle: [a.reference, formatDateTimeFr(a.scheduled_at)].filter(Boolean).join(" · "),
    to: `/rendez-vous/${a.id}`,
  }));
}
